import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import AddFertilizerMaster from "./AddFertilizerMaster";
import {
  getAllFertilizers,
  deleteFertilizer,
  getFertilizerBackups,
} from "../lib/api";

const FertilizerMasterList = () => {
  const getToken = () => localStorage.getItem("token") || localStorage.getItem("authToken");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // State
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search, Filter & Pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [chlorideFilter, setChlorideFilter] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("DESC");

  // Drawer & Modal State
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);

  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Backups / Audit Modal State
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [backupFertilizer, setBackupFertilizer] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);

  /* ---------------- Fetch list ---------------- */
  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();

      const params = {
        page: currentPage,
        limit: entriesPerPage,
        search: searchTerm.trim() || undefined,
        sortBy,
        sortOrder,
      };

      if (unitFilter) params.default_unit_for_price = unitFilter;
      if (chlorideFilter !== "") params.is_chloride_containing = chlorideFilter;

      const res = await getAllFertilizers(params, token);
      const data = res?.data || [];
      setRecords(data);
      if (res?.pagination?.total !== undefined) {
        setTotalRecords(res.pagination.total);
      } else {
        setTotalRecords(data.length);
      }
    } catch (err) {
      console.error("Error fetching fertilizers:", err);
      toast.error(err?.response?.data?.message || "Failed to load fertilizer master records.");
    } finally {
      setLoading(false);
    }
  }, [currentPage, entriesPerPage, searchTerm, unitFilter, chlorideFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, unitFilter, chlorideFilter, entriesPerPage]);

  /* ---------------- Sorting ---------------- */
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortBy(field);
      setSortOrder("ASC");
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return "lucide:chevrons-up-down";
    return sortOrder === "ASC" ? "lucide:chevron-up" : "lucide:chevron-down";
  };

  /* ---------------- Add / Edit ---------------- */
  const handleAddNew = () => {
    setRecordToEdit(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (record) => {
    setRecordToEdit(record);
    setIsDrawerOpen(true);
  };

  /* ---------------- Delete ---------------- */
  const confirmDelete = (record) => {
    setRecordToDelete(record);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setRecordToDelete(null);
  };

  const handleDelete = async () => {
    if (!recordToDelete) return;
    try {
      setDeleting(true);
      const token = getToken();
      const res = await deleteFertilizer(recordToDelete.id, token);
      toast.success(res?.message || "Fertilizer deleted successfully (backup archived).");
      setShowDeleteModal(false);
      setRecordToDelete(null);
      fetchRecords();
    } catch (err) {
      console.error("Error deleting fertilizer:", err);
      toast.error(err?.response?.data?.message || err.message || "Failed to delete fertilizer.");
    } finally {
      setDeleting(false);
    }
  };

  /* ---------------- Filters ---------------- */
  const resetFilters = () => {
    setSearchTerm("");
    setUnitFilter("");
    setChlorideFilter("");
    setSortBy("created_at");
    setSortOrder("DESC");
    setCurrentPage(1);
  };

  const hasActiveFilters = Boolean(searchTerm || unitFilter || chlorideFilter !== "");

  /* ---------------- Pagination ---------------- */
  const totalPages = entriesPerPage === -1 ? 1 : Math.max(1, Math.ceil(totalRecords / entriesPerPage));
  const startIndex = entriesPerPage === -1 ? 0 : (currentPage - 1) * entriesPerPage;
  const endIndex = entriesPerPage === -1 ? records.length : Math.min(startIndex + entriesPerPage, totalRecords);

  return (
    <>
      <div className="card p-0 radius-12">
        {/* ─── Header (single-line flex) ───────────────────────────── */}
        <div className="card-header border-bottom bg-base py-16 px-24">
          <div
            className="d-flex align-items-center gap-2 flex-nowrap"
            style={{ minWidth: 0 }}
          >
            {/* Show entries */}
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <span className="text-sm fw-medium text-secondary-light text-nowrap">Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 72 }}
                value={entriesPerPage}
                onChange={(e) => setEntriesPerPage(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={-1}>All</option>
              </select>
            </div>

            {/* Search — the only flexible item */}
            <div className="navbar-search" style={{ flex: "1 1 200px", minWidth: 160, maxWidth: 320 }}>
              <input
                className="bg-base h-40-px"
                style={{ width: "100%" }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search generic or brand name..."
              />
              <Icon icon="ion:search-outline" className="icon" />
            </div>

            {/* Reset */}
            {hasActiveFilters && (
              <button
                type="button"
                className="btn btn-sm btn-light px-12 py-10 radius-8 d-flex align-items-center gap-2 flex-shrink-0"
                onClick={resetFilters}
                title="Reset filters"
              >
                <Icon icon="lucide:rotate-ccw" style={{ fontSize: 15 }} />
                <span className="text-sm text-nowrap">Reset</span>
              </button>
            )}

            {/* Add — pushed to right */}
            <button
              onClick={handleAddNew}
              className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2 flex-shrink-0 ms-auto"
            >
              <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
              <span className="text-nowrap">Add Fertilizer</span>
            </button>
          </div>
        </div>

        {/* ─── Table ──────────────────────────────────────────────── */}
        {loading ? (
          <div className="text-center py-4 d-flex align-items-center justify-content-center gap-2">
            <span className="spinner-border text-primary spinner-border-sm" role="status" />
            <span className="text-secondary-light">Loading fertilizers...</span>
          </div>
        ) : (
          <>
            <div className="table-responsive p-16">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th
                      className="cursor-pointer user-select-none"
                      onClick={() => handleSort("generic_name")}
                      style={{ minWidth: 200 }}
                    >
                      <div className="d-flex align-items-center gap-1">
                        <span>Fertilizer Name</span>
                        <Icon icon={getSortIcon("generic_name")} style={{ fontSize: 14 }} className="text-secondary-light" />
                      </div>
                    </th>
                    <th
                      className="cursor-pointer user-select-none text-center"
                      onClick={() => handleSort("default_unit_for_price")}
                      style={{ width: 120 }}
                    >
                      <div className="d-flex align-items-center justify-content-center gap-1">
                        <span>Price Unit</span>
                        <Icon icon={getSortIcon("default_unit_for_price")} style={{ fontSize: 14 }} className="text-secondary-light" />
                      </div>
                    </th>
                    <th className="text-center" style={{ width: 130 }}>Chloride</th>
                    <th className="text-center" style={{ minWidth: 180 }}>N - P₂O₅ - K₂O (%)</th>
                    <th className="text-center" style={{ width: 150 }}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {records.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        {hasActiveFilters
                          ? "No records match your filters. Try resetting."
                          : "No fertilizer records found. Add the first one."}
                      </td>
                    </tr>
                  ) : (
                    records.map((item) => {
                      const hasSecondary =
                        item.s_pct || item.ca_pct || item.mg_pct || item.zn_pct || item.b_pct;

                      return (
                        <tr key={item.id}>
                          {/* Name & Brand */}
                          <td>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <div className="text-md fw-medium text-primary-light">
                                {item.generic_name}
                              </div>
                            </div>
                            {item.category && (
                              <div className="mt-1">
                                <span
                                  className="badge bg-primary-50 text-primary-600 border border-primary-100 radius-4 px-8 py-2 text-xs"
                                  style={{ fontSize: "0.72rem" }}
                                >
                                  {item.category}
                                </span>
                              </div>
                            )}
                            {item.brand_name && (
                              <div className="text-xs text-secondary-light mt-1">
                                <span className="fw-semibold">Brand:</span>{" "}
                                {item.brand_name}
                              </div>
                            )}
                          </td>

                          {/* Price Unit */}
                          <td className="text-center">
                            <span className="px-12 py-4 radius-4 fw-medium text-xs bg-secondary text-white">
                              {item.default_unit_for_price || "per kg"}
                            </span>
                          </td>

                          {/* Chloride */}
                          <td className="text-center">
                            {item.is_chloride_containing ? (
                              <span className="px-12 py-4 radius-4 fw-medium text-xs bg-warning-focus text-warning-600 border border-warning-main">
                                Contains Cl
                              </span>
                            ) : (
                              <span className="px-12 py-4 radius-4 fw-medium text-xs bg-success-focus text-success-600 border border-success-main">
                                Cl-Free
                              </span>
                            )}
                          </td>

                          {/* Primary NPK */}
                          <td className="text-center">
                            <div className="d-flex align-items-center justify-content-center gap-1 flex-wrap">
                              <span className="px-8 py-2 radius-4 fw-medium text-xs bg-success-focus text-success-600 border border-success-main">
                                N {item.n_pct !== null ? `${item.n_pct}%` : "0%"}
                              </span>
                              <span className="px-8 py-2 radius-4 fw-medium text-xs bg-primary-focus text-primary-600 border border-primary-main">
                                P {item.p2o5_pct !== null ? `${item.p2o5_pct}%` : "0%"}
                              </span>
                              <span className="px-8 py-2 radius-4 fw-medium text-xs bg-warning-focus text-warning-600 border border-warning-main">
                                K {item.k2o_pct !== null ? `${item.k2o_pct}%` : "0%"}
                              </span>
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="text-center">
                            <div className="d-flex align-items-center gap-10 justify-content-center">
                              <button
                                type="button"
                                className="edit-btn bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                onClick={() => handleEdit(item)}
                                title="Edit Fertilizer"
                              >
                                <Icon icon="lucide:edit" className="menu-icon" />
                              </button>

                              {user?.IsDeleteAllowed !== false && (
                                <button
                                  type="button"
                                  className="delete-btn bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                  onClick={() => confirmDelete(item)}
                                  title="Delete Fertilizer"
                                >
                                  <Icon icon="lucide:trash-2" className="menu-icon" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ─── Pagination ─────────────────────────────────────── */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 m-16">
              <span className="text-secondary-light">
                {totalRecords === 0
                  ? "Showing 0 entries"
                  : `Showing ${startIndex + 1} to ${endIndex} of ${totalRecords} entries`}
              </span>

              {totalPages > 1 && (
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-light btn-sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="align-self-center text-secondary-light">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    className="btn btn-light btn-sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ─── Add/Edit Drawer ───────────────────────────────────────── */}
      <AddFertilizerMaster
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSuccess={() => fetchRecords()}
        recordToEdit={recordToEdit}
      />

      {/* ─── Delete Confirmation Modal ─────────────────────────────── */}
      {showDeleteModal && recordToDelete && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(2px)", zIndex: 1060 }}
          onClick={closeDeleteModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: 440 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: 14 }}>
              <div className="modal-body text-center" style={{ padding: "32px 28px 20px" }}>
                <div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 18,
                  }}
                >
                  <Icon icon="lucide:trash-2" style={{ fontSize: 26, color: "#dc2626" }} />
                </div>

                <h5
                  className="fw-semibold mb-2"
                  style={{ fontSize: "1.05rem", color: "#0f172a", letterSpacing: "-0.01em" }}
                >
                  Delete this fertilizer?
                </h5>

                <p className="mb-3" style={{ fontSize: "0.9rem", color: "#64748b", lineHeight: 1.55 }}>
                  You are about to delete{" "}
                  <strong style={{ color: "#0f172a", fontWeight: 600 }}>
                    {recordToDelete.generic_name}
                  </strong>
                  {recordToDelete.brand_name ? ` (${recordToDelete.brand_name})` : ""}.
                  This action cannot be undone.
                </p>

                <div
                  className="d-flex align-items-start gap-2 text-start"
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontSize: "0.78rem",
                    color: "#64748b",
                    lineHeight: 1.5,
                  }}
                >
                  <Icon icon="lucide:shield-check" style={{ fontSize: 15, color: "#0ea5e9", marginTop: 2, flexShrink: 0 }} />
                  <span>
                    A backup snapshot will be archived automatically before removal, so the data
                    can be recovered from the audit log.
                  </span>
                </div>
              </div>

              <div className="modal-footer border-0 d-flex gap-2" style={{ padding: "4px 28px 24px" }}>
                <button
                  type="button"
                  className="btn flex-fill fw-medium"
                  onClick={closeDeleteModal}
                  disabled={deleting}
                  style={{
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    color: "#334155",
                    padding: "10px 16px",
                    borderRadius: 8,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn flex-fill fw-medium d-inline-flex align-items-center justify-content-center gap-2"
                  onClick={handleDelete}
                  disabled={deleting}
                  style={{
                    background: "#dc2626",
                    border: "1px solid #dc2626",
                    color: "#fff",
                    padding: "10px 16px",
                    borderRadius: 8,
                  }}
                >
                  {deleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" />
                      <span>Deleting…</span>
                    </>
                  ) : (
                    <>
                      <Icon icon="lucide:trash-2" style={{ fontSize: 16 }} />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Backup / Audit History Modal ──────────────────────────── */}
      {showBackupModal && backupFertilizer && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(2px)", zIndex: 1060 }}
          onClick={() => setShowBackupModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: 720 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: 14 }}>
              {/* Header */}
              <div
                className="d-flex align-items-start justify-content-between gap-3 px-4 pt-4 pb-3"
                style={{ borderBottom: "1px solid #e2e8f0" }}
              >
                <div className="d-flex align-items-start gap-3">
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 10,
                      background: "#eff6ff",
                      border: "1px solid #dbeafe",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon icon="lucide:history" style={{ fontSize: 20, color: "#2563eb" }} />
                  </div>
                  <div>
                    <h5 className="fw-semibold mb-0" style={{ fontSize: "1rem", color: "#0f172a" }}>
                      Revision History
                    </h5>
                    <div className="text-xs" style={{ color: "#64748b" }}>
                      {backupFertilizer.generic_name}
                      {backupFertilizer.brand_name ? ` · ${backupFertilizer.brand_name}` : ""}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowBackupModal(false)}
                  aria-label="Close"
                />
              </div>

              {/* Body */}
              <div className="modal-body" style={{ padding: 20, maxHeight: 480, overflowY: "auto" }}>
                {loadingBackups ? (
                  <div className="text-center py-5">
                    <span className="spinner-border text-primary spinner-border-sm mb-2" role="status" />
                    <div className="text-sm text-secondary-light">Loading backup history…</div>
                  </div>
                ) : backups.length === 0 ? (
                  <div className="text-center py-5">
                    <Icon icon="lucide:inbox" style={{ fontSize: 32, color: "#94a3b8" }} className="mb-2" />
                    <div className="text-sm" style={{ color: "#0f172a" }}>
                      No previous revisions found.
                    </div>
                    <div className="text-xs mt-1" style={{ color: "#94a3b8" }}>
                      Snapshots are created automatically on each update or delete.
                    </div>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {backups.map((b, idx) => {
                      const isDelete = b.backup_action === "DELETE";
                      return (
                        <div
                          key={b.id || idx}
                          style={{
                            border: "1px solid #e2e8f0",
                            borderRadius: 10,
                            padding: 14,
                            background: "#f8fafc",
                          }}
                        >
                          {/* Top row */}
                          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className="fw-semibold"
                                style={{
                                  fontSize: "0.68rem",
                                  padding: "3px 8px",
                                  borderRadius: 4,
                                  background: isDelete ? "#fee2e2" : "#dbeafe",
                                  color: isDelete ? "#b91c1c" : "#1d4ed8",
                                  letterSpacing: "0.03em",
                                }}
                              >
                                {b.backup_action || "UPDATE"}
                              </span>
                              <span className="text-xs" style={{ color: "#64748b" }}>
                                {b.backup_created_at
                                  ? new Date(b.backup_created_at).toLocaleString()
                                  : "—"}
                              </span>
                            </div>
                            <span className="text-xs" style={{ color: "#94a3b8" }}>
                              By <strong style={{ color: "#334155" }}>{b.backup_created_by || "Admin"}</strong>
                            </span>
                          </div>

                          {/* Snapshot details */}
                          <div className="row g-2 mt-3 pt-3" style={{ borderTop: "1px dashed #e2e8f0" }}>
                            <div className="col-sm-6">
                              <div className="text-xs" style={{ color: "#94a3b8" }}>Generic Name</div>
                              <div className="text-sm fw-medium" style={{ color: "#0f172a" }}>
                                {b.generic_name || "—"}
                              </div>
                            </div>
                            <div className="col-sm-6">
                              <div className="text-xs" style={{ color: "#94a3b8" }}>Brand</div>
                              <div className="text-sm fw-medium" style={{ color: "#0f172a" }}>
                                {b.brand_name || "None"}
                              </div>
                            </div>
                            <div className="col-sm-6">
                              <div className="text-xs" style={{ color: "#94a3b8" }}>Price Unit / Chloride</div>
                              <div className="text-sm" style={{ color: "#0f172a" }}>
                                {b.default_unit_for_price || "—"} ·{" "}
                                {b.is_chloride_containing ? "Contains Cl" : "Chloride Free"}
                              </div>
                            </div>
                            <div className="col-sm-6">
                              <div className="text-xs" style={{ color: "#94a3b8" }}>N – P₂O₅ – K₂O (%)</div>
                              <div className="text-sm" style={{ color: "#0f172a" }}>
                                {b.n_pct ?? 0}% · {b.p2o5_pct ?? 0}% · {b.k2o_pct ?? 0}%
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div
                className="d-flex justify-content-end gap-2 px-4 pb-4"
                style={{ paddingTop: 6 }}
              >
                <button
                  type="button"
                  className="btn fw-medium"
                  onClick={() => setShowBackupModal(false)}
                  style={{
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    color: "#334155",
                    padding: "8px 16px",
                    borderRadius: 8,
                    fontSize: "0.85rem",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FertilizerMasterList;